<svelte:options accessors />

<script lang="ts">
	import Input from '$lib/components/Global/Fields/Input.svelte';
	import { createEventDispatcher, onMount, type ComponentProps } from 'svelte';
	import { type StepUpdater } from '../../Stepper/stepper';
	import FieldWrapper from './FieldWrapper.svelte';
	import ChevronRight from '$lib/assets/images/icons/chevron-right.svg?component';
	import { hasTouchSupport } from '$lib/utils/helpers/hasTouchSupport';

	type Option = { label: string; value: string | number | boolean };

	export let name: string;
	export let options: Option[] = [];
	export let placeholder: string = '';
	export let onUpdate: StepUpdater<string> | null = null;
	export let variant: 'default' | 'grid-only' = 'default';

	interface $$Props extends ComponentProps<Input> {
		name: string;
		onUpdate?: StepUpdater<string> | null;
		variant?: 'default' | 'grid-only';
		options: { label: string; value: string | number | boolean }[];
	}

	export let value: string = '';
	$: selected = value !== '' && value !== null;
	let disabled = false;

	export async function safeChange(callback: () => void, delay = 500) {
		if (disabled) return;
		disabled = true;

		if (delay > 0) {
			await new Promise((resolve) =>
				setTimeout(() => {
					disabled = false;
					resolve(null);
				}, delay)
			);
		}

		callback();
		disabled = false;
	}

	let selectEntered = false;
	const dispatch = createEventDispatcher();

	let isPhone = false;
	onMount(() => {
		isPhone = hasTouchSupport();
	});
</script>

<FieldWrapper
	{name}
	{onUpdate}
	let:handleUpdate
	let:error
	on:value={(e) => (value = e.detail || '')}
>
	<div>
		{#if variant === 'default'}
			<div class="relative lg:hidden">
				<select
					{name}
					{value}
					{disabled}
					class="w-full cursor-pointer appearance-none rounded-xl p-5 text-lg font-semibold outline-none transition-colors duration-300 disabled:opacity-100 {!selected
						? 'bg-inactive text-slate-inactive'
						: 'bg-secondary-light text-dark'}"
					on:change={(e) => {
						handleUpdate(e);
						const target = e.target;
						target.dataset.change = true;
					}}
					on:click={(e) => {
						if (!disabled) selectEntered = true;
						if (!isPhone && !e.target.dataset.change) {
							e.target?.dispatchEvent(new Event('change'));
						}
						e.target.dataset.change = false;
					}}
					on:change
					on:blur={() => {
						if (!disabled) selectEntered = false;
					}}
					{placeholder}
				>
					<option value="">{placeholder}</option>
					{#each options as option}
						<option value={option.value}>{@html option.label}</option>
					{/each}
				</select>
				<span
					class="pointer-events-none absolute right-0 top-0 flex h-full w-16 items-center justify-center"
				>
					<ChevronRight
						class="h-4 {!selectEntered
							? 'rotate-90'
							: 'rotate-[270deg]'} stroke-dark stroke-[8px] transition-transform"
					/>
				</span>
			</div>
		{/if}
		<div
			class="shrink-0 grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-x-11 lg:gap-y-7 {variant ===
			'grid-only'
				? 'grid'
				: 'hidden lg:grid'}"
		>
			{#each options as option}
				<button
					on:click={(e) => {
						handleUpdate(option.value);
						dispatch('change', option.value);
					}}
				>
					<slot name="grid-option" {option} {value}>
						<div
							class:bg-secondary-light={value === option.value}
							class:font-semibold={value === option.value}
							class="flex h-14 w-full items-center justify-center rounded-xl bg-inactive text-base/5 transition hover:scale-95 hover:bg-secondary-light hover:font-semibold lg:relative lg:h-[156px] lg:w-[172px] lg:text-left"
						>
							<span class="lg:absolute lg:bottom-6 lg:left-6">{@html option.label}</span>
						</div>
					</slot>
				</button>
			{/each}
		</div>
	</div>
</FieldWrapper>
